<template>
	<div>
		<div class="header-section">
			<div>
				<h1>Составление заявки</h1>
				<div v-if="selectedSupplier" class="supplier-info">
					<strong>Поставщик:</strong> {{ selectedSupplier.name }}
				</div>
			</div>
			<PrimeButton 
				label="Назад к архиву" 
				icon="pi pi-arrow-left" 
				severity="secondary"
				@click="router.push('/request-archives')"
			/>
		</div>

		<div class="search-section">
			<h3>Поиск запчастей</h3>
			<PrimeInputText 
				v-model="searchQuery" 
				placeholder="Поиск по наименованию..." 
				class="search-input"
				@input="performSearch"
			/>
		</div>

		<div v-if="searchResults.length > 0" class="search-results">
			<h3>Результаты поиска</h3>
			<table>
				<thead>
					<tr>
						<th>#</th>
						<th>Наименование</th>
						<th>Марка</th>
						<th>Мера количества</th>
						<th>Действие</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, index) in searchResults" :key="item.id">
						<td>{{ index + 1 }}</td>
						<td>{{ item.name }}</td>
						<td>{{ item.carBrand }}</td>
						<td>{{ item.quantifier }}</td>
						<td>
							<PrimeButton 
								label="Добавить" 
								icon="pi pi-plus" 
								size="small"
								@click="addToRequest(item)" 
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div v-else-if="searchQuery && searchQuery.length > 0" class="no-results">
			Ничего не найдено
		</div>

		<div v-if="requestItems.length > 0" class="request-section">
			<h3>Заявка</h3>
			<table>
				<thead>
					<tr>
						<th>Наименование</th>
						<th>Марка</th>
						<th>Мера количества</th>
						<th>Количество</th>
						<th>Удалить</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, index) in requestItems" :key="item.id || index">
						<td>
							<PrimeInputText 
								v-model="item.name" 
								style="width: 400px; min-width: 400px;"
								placeholder="Наименование"
							/>
						</td>
						<td>
							<PrimeInputText 
								v-model="item.carBrand" 
								style="width: 150px;"
								placeholder="Марка"
							/>
						</td>
						<td>
							<PrimeInputText 
								v-model="item.quantifier" 
								style="width: 150px;"
								placeholder="Мера количества"
							/>
						</td>
						<td>
							<PrimeInputText 
								type="number" 
								v-model.number="item.quantity" 
								style="width: 100px;"
								min="1"
							/>
						</td>
						<td>
							<PrimeButton 
								icon="pi pi-trash" 
								severity="danger" 
								size="small"
								@click="removeRequestItem(index)" 
							/>
						</td>
					</tr>
				</tbody>
			</table>

			<div class="request-actions">
				<div class="action-buttons">
					<PrimeButton 
						label="Очистить все" 
						icon="pi pi-trash" 
						severity="secondary"
						@click="clearRequest" 
					/>
					<PrimeButton 
						label="Скачать файл" 
						icon="pi pi-file-excel" 
						:disabled="requestItems.length === 0"
						@click="submitRequest" 
					/>
					<PrimeButton 
						label="Отправить поставщику" 
						icon="pi pi-send" 
						:disabled="requestItems.length === 0 || !selectedSupplier"
						@click="sendToSupplier" 
					/>
				</div>
			</div>
		</div>

		<div v-else class="empty-state">
			<p>В заявке нет позиций. Найдите и добавьте позиции для составления заявки.</p>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { api } from "../api";
import { useToast } from 'primevue/usetoast';

const route = useRoute();
const router = useRouter();
const searchQuery = ref("");
const searchResults = ref([]);
const allSpares = ref([]);
const requestItems = ref([]);
const suppliers = ref([]);
const selectedSupplier = ref(null);
const currentRequestId = ref(null);
const toast = useToast();
const isSaving = ref(false);
const isInitialLoad = ref(true);
let saveTimeout = null;

// Load all spare components and suppliers
onMounted(async () => {
	api.get("/sparecomponents").then(r => allSpares.value = r.data);
	
	// Load suppliers
	const suppliersResponse = await api.get("/suppliers");
	suppliers.value = suppliersResponse.data;
	
	// Load request by ID from route
	const requestId = route.params.id;
	if (requestId) {
		try {
			const response = await api.get(`/requests/${requestId}`);
			const request = response.data;
			currentRequestId.value = request.id;
			
			// Only allow editing pending requests
			if (request.status !== 0) {
				toast.add({ 
					severity: 'warn', 
					summary: 'Предупреждение', 
					detail: 'Эта заявка уже завершена и не может быть отредактирована', 
					life: 3000 
				});
				router.push('/request-archives');
				return;
			}
			
			// Load items from request
			requestItems.value = request.items.map(item => ({
				id: item.id,
				name: item.spareComponentName || '',
				carBrand: item.carBrand || '',
				quantifier: item.quantifier || '',
				quantity: item.quantity || 1
			}));
			
			// Set selected supplier
			selectedSupplier.value = suppliers.value.find(s => s.id === request.supplierId);
			
			// Mark initial load as complete after a short delay to allow watcher to be set up
			nextTick(() => {
				setTimeout(() => {
					isInitialLoad.value = false;
				}, 500);
			});
		} catch (err) {
			console.error(err);
			toast.add({ 
				severity: 'error', 
				summary: 'Ошибка', 
				detail: 'Не удалось загрузить заявку', 
				life: 3000 
			});
			router.push('/request-archives');
		}
	} else {
		// No request ID - redirect to archives
		router.push('/request-archives');
	}
});

// Immediate save function (no debounce)
const saveRequestImmediately = async () => {
	if (isSaving.value || !currentRequestId.value || !selectedSupplier.value || isInitialLoad.value) {
		return;
	}
	
	isSaving.value = true;
	try {
		// Update existing request
		await api.put(`/requests/${currentRequestId.value}`, {
			supplierId: selectedSupplier.value.id,
			items: requestItems.value.map(x => ({
				spareComponentName: x.name || '',
				carBrand: x.carBrand || '',
				quantifier: x.quantifier || '',
				quantity: x.quantity || 1
			}))
		});
		console.log("Saved successfully");
	} catch (err) {
		console.error("Save failed:", err);
		throw err; // Re-throw to allow caller to handle
	} finally {
		isSaving.value = false;
	}
};

// Auto-save function with debouncing (for field edits)
const autoSave = () => {
	if (isSaving.value || !currentRequestId.value || !selectedSupplier.value || isInitialLoad.value) {
		return;
	}

	// Clear existing timeout
	if (saveTimeout) {
		clearTimeout(saveTimeout);
	}

	// Debounce: wait 1 second after last change before saving
	saveTimeout = setTimeout(async () => {
		if (isSaving.value || !currentRequestId.value || !selectedSupplier.value || isInitialLoad.value) return;
		
		isSaving.value = true;
		try {
			// Update existing request
			await api.put(`/requests/${currentRequestId.value}`, {
				supplierId: selectedSupplier.value.id,
				items: requestItems.value.map(x => ({
					spareComponentName: x.name || '',
					carBrand: x.carBrand || '',
					quantifier: x.quantifier || '',
					quantity: x.quantity || 1
				}))
			});
			console.log("Auto-saved successfully");
		} catch (err) {
			console.error("Auto-save failed:", err);
			// Don't show error toast for auto-save failures to avoid annoying the user
		} finally {
			isSaving.value = false;
		}
	}, 1000);
};

// Watch for changes to requestItems and auto-save
watch(requestItems, () => {
	if (currentRequestId.value && selectedSupplier.value && !isInitialLoad.value) {
		autoSave();
	}
}, { deep: true });


const normalizeSearchText = (text) => {
	if (!text) return "";

	return text
		.toString()
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[\u200B-\u200D\uFEFF]/g, "")
		.replace(/\s+/g, " ")
		.trim();
};

// Check if text starts with query (case-insensitive)
const startsWith = (text, query) => {
	const normalizedText = normalizeSearchText(text);
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedText || !normalizedQuery) return false;
	return normalizedText.startsWith(normalizedQuery);
};

// Check if text exactly matches query after trimming and case normalization
const exactMatch = (text, query) => {
	const normalizedText = normalizeSearchText(text);
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedText || !normalizedQuery) return false;
	return normalizedText === normalizedQuery;
};

// Check if query appears anywhere in the normalized name
const containsMatch = (text, query) => {
	const normalizedText = normalizeSearchText(text);
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedText || !normalizedQuery) return false;
	return normalizedText.includes(normalizedQuery);
};

// Calculate Levenshtein distance between two strings
const levenshteinDistance = (str1, str2) => {
	const len1 = str1.length;
	const len2 = str2.length;
	
	// Create a matrix
	const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
	
	// Initialize first row and column
	for (let i = 0; i <= len1; i++) matrix[i][0] = i;
	for (let j = 0; j <= len2; j++) matrix[0][j] = j;
	
	// Fill the matrix
	for (let i = 1; i <= len1; i++) {
		for (let j = 1; j <= len2; j++) {
			const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
			matrix[i][j] = Math.min(
				matrix[i - 1][j] + 1,      // deletion
				matrix[i][j - 1] + 1,      // insertion
				matrix[i - 1][j - 1] + cost // substitution
			);
		}
	}
	
	return matrix[len1][len2];
};

// Fuzzy search function using Levenshtein distance
const fuzzyMatch = (text, query) => {
	if (!query || !text) return false;
	const lowerText = normalizeSearchText(text);
	const lowerQuery = normalizeSearchText(query);
	
	// Check if entire name is similar
	const fullDistance = levenshteinDistance(lowerText, lowerQuery);
	const maxFullDistance = Math.max(1, Math.min(3, Math.floor(lowerQuery.length / 4) + 1));
	if (fullDistance <= maxFullDistance) {
		return true;
	}
	
	// Check if query appears as a substring with typos
	// Slide a window through the text and check similarity
	const queryLen = lowerQuery.length;
	for (let i = 0; i <= lowerText.length - queryLen; i++) {
		const substring = lowerText.substring(i, i + queryLen);
		const distance = levenshteinDistance(substring, lowerQuery);
		// Allow 1-2 character differences for substring matches
		const maxSubstringDistance = Math.max(1, Math.min(2, Math.floor(queryLen / 5) + 1));
		if (distance <= maxSubstringDistance) {
			return true;
		}
	}
	
	return false;
};

// Check if name starts with query
const hasPrefixMatch = (spare, query) => {
	return startsWith(spare.name, query);
};

// Check if name matches via fuzzy search
const hasFuzzyMatch = (spare, query) => {
	return fuzzyMatch(spare.name, query);
};

const performSearch = () => {
	if (!searchQuery.value || searchQuery.value.length < 2) {
		searchResults.value = [];
		return;
	}

	const query = searchQuery.value.trim();
	
	// Separate items by relevance so exact matches always stay at the top
	const exactMatches = [];
	const prefixMatches = [];
	const containsMatches = [];
	const fuzzyMatches = [];
	
	allSpares.value.forEach(spare => {
		if (exactMatch(spare.name, query)) {
			exactMatches.push(spare);
		} else if (hasPrefixMatch(spare, query)) {
			prefixMatches.push(spare);
		} else if (containsMatch(spare.name, query)) {
			containsMatches.push(spare);
		} else if (hasFuzzyMatch(spare, query)) {
			fuzzyMatches.push(spare);
		}
	});
	
	// Combine by relevance: exact, then prefix, then contains, then fuzzy
	// Limit to 20 results total
	searchResults.value = [...exactMatches, ...prefixMatches, ...containsMatches, ...fuzzyMatches].slice(0, 20);
};

	const addToRequest = async (spare) => {
		const existing = requestItems.value.find(x => x.id === spare.id);
		if (existing) {
			existing.quantity += 1;
			toast.add({ severity: 'info', summary: 'Обновлено', detail: 'Количество увеличено', life: 2000 });
		} else {
			requestItems.value.push({ ...spare, quantity: 1 });
			toast.add({ severity: 'success', summary: 'Добавлено', detail: 'Позиция добавлена в заявку', life: 2000 });
		}
		
		// Save immediately when adding/updating items
		try {
			await saveRequestImmediately();
		} catch (err) {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить изменения', life: 3000 });
		}
	};

	const removeRequestItem = async (index) => {
		requestItems.value.splice(index, 1);
		toast.add({ severity: 'info', summary: 'Удалено', detail: 'Позиция удалена из заявки', life: 2000 });
		
		// Save immediately when removing items
		try {
			await saveRequestImmediately();
		} catch (err) {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить изменения', life: 3000 });
		}
	};

	const clearRequest = async () => {
		requestItems.value = [];
		toast.add({ severity: 'info', summary: 'Очищено', detail: 'Заявка очищена', life: 2000 });
		
		// Save immediately when clearing items
		try {
			await saveRequestImmediately();
		} catch (err) {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить изменения', life: 3000 });
		}
	};

	// Navigation guard to ensure pending saves complete
	onBeforeRouteLeave(async (to, from, next) => {
		// Clear any pending debounced saves
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = null;
		}
		
		// Wait for any in-progress save to complete
		if (isSaving.value) {
			// Wait for save to complete (with timeout)
			const maxWait = 5000; // 5 seconds max wait
			const startTime = Date.now();
			while (isSaving.value && (Date.now() - startTime) < maxWait) {
				await new Promise(resolve => setTimeout(resolve, 100));
			}
		}
		
		// If there are unsaved changes, save them now
		if (currentRequestId.value && selectedSupplier.value && !isInitialLoad.value) {
			try {
				await saveRequestImmediately();
			} catch (err) {
				console.error("Failed to save before navigation:", err);
				// Continue navigation anyway - user can come back and edit
			}
		}
		
		next();
	});

	// Cleanup on component unmount
	onBeforeUnmount(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
		}
	});

const downloadExcel = async (id) => {
	try {
		const response = await api.get(`/requests/${id}/export`, {
			responseType: 'blob'
		});
		
		const blob = new Blob([response.data], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `Request_${id}.xlsx`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error(err);
		toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить файл Excel', life: 3000 });
		throw err;
	}
};

	const submitRequest = async () => {
		if (!currentRequestId.value) {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Заявка не найдена', life: 3000 });
			return;
		}

		try {
			await downloadExcel(currentRequestId.value);
			toast.add({ severity: 'success', summary: 'Успешно', detail: 'Файл Excel загружен', life: 3000 });
		} catch (err) {
			console.error(err);
			const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Не удалось загрузить файл Excel';
			toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 3000 });
		}
	};

	const sendToSupplier = async () => {
		if (!selectedSupplier.value) {
			toast.add({ severity: 'warn', summary: 'Проверка', detail: 'Пожалуйста, выберите поставщика', life: 3000 });
			return;
		}

		if (!currentRequestId.value) {
			toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Заявка не найдена', life: 3000 });
			return;
		}

		try {
			// Update request first
			await api.put(`/requests/${currentRequestId.value}`, {
				supplierId: selectedSupplier.value.id,
				items: requestItems.value.map(x => ({
					spareComponentName: x.name || '',
					carBrand: x.carBrand || '',
					quantifier: x.quantifier || '',
					quantity: x.quantity || 1
				}))
			});
		
			// Then send email
			await api.post(`/requests/${currentRequestId.value}/send-email`);
			
			toast.add({ 
				severity: 'success', 
				summary: 'Успешно', 
				detail: `Заявка отправлена на ${selectedSupplier.value.email}`, 
				life: 3000 
			});
			
			// Navigate back to archives
			router.push('/request-archives');
		} catch (err) {
			console.error(err);
			const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Не удалось отправить заявку';
			toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 3000 });
		}
	};
</script>

<style scoped>
.search-section {
	background: white;
	padding: 1.5rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	margin-bottom: 2rem;
}

.search-section h3 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.search-input {
	width: 100%;
	font-size: 1rem;
	padding: 0.75rem;
}

.search-results {
	background: white;
	padding: 1.5rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	margin-bottom: 2rem;
}

.search-results h3 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.no-results {
	background: white;
	padding: 2rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	text-align: center;
	color: #666;
	margin-bottom: 2rem;
}

.request-section {
	background: white;
	padding: 1.5rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.request-section h3 {
	margin-bottom: 1rem;
	color: #2c3e50;
}

.request-section table {
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 1rem;
}

.request-section table th {
	background-color: #f8f9fa;
	padding: 0.75rem;
	text-align: left;
	border-bottom: 2px solid #dee2e6;
	font-weight: 600;
}

.request-section table td {
	padding: 0.5rem;
	border-bottom: 1px solid #dee2e6;
}

.request-actions {
	margin-top: 1.5rem;
	padding-top: 1.5rem;
	border-top: 1px solid #eee;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
}

.supplier-select {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.supplier-dropdown {
	min-width: 200px;
}

.action-buttons {
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.empty-state {
	background: white;
	padding: 3rem;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	text-align: center;
	color: #666;
}

.header-section {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 1.5rem;
}

.supplier-info {
	margin-top: 0.5rem;
	color: #2c3e50;
	font-size: 1rem;
}
</style>
